/**
 * «Mobil ilovaga ulash» — kirgan foydalanuvchi telefon ilovasini QR orqali ulaydi.
 *
 * Tugma (yuqori o'ng burchakda) bosilganda oyna ochiladi: server 5 daqiqalik bir
 * martalik QR token beradi, ilova uni skanerlaydi va shu hisob bilan kirib ketadi.
 * Oyna holatni har 2 soniyada so'raydi («ulandi» deb ko'rsatadi), vaqt tugasa yangi QR
 * so'raladi, oyna yopilsa token darhol bekor qilinadi.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { authApi } from "@/shared/api/endpoints";
import type { QrLinkTicket } from "@/shared/api/types";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";
import { Modal } from "@/shared/ui/Modal";

type Phase = "loading" | "ready" | "expired" | "linked" | "error";

function formatTime(seconds: number): string {
  const safe = Math.max(0, seconds);
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

function MobileLinkDialog({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [ticket, setTicket] = useState<QrLinkTicket | null>(null);
  const [left, setLeft] = useState(0);
  const [device, setDevice] = useState("");
  const [message, setMessage] = useState("");
  const current = useRef<string | null>(null);

  const issue = useCallback(async () => {
    setPhase("loading");
    setMessage("");
    try {
      const data = await authApi.qrCreate();
      current.current = data.ticket;
      setTicket(data);
      setLeft(data.expires_in);
      setPhase("ready");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "QR-kodni olib bo'lmadi.");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    void issue();
    return () => {
      /* Oyna yopilganda token yaroqsiz bo'ladi — begona qo'lga tushmasin. */
      if (current.current) void authApi.qrCancel(current.current).catch(() => undefined);
    };
  }, [issue]);

  /* Teskari sanoq. */
  useEffect(() => {
    if (phase !== "ready") return;
    const timer = window.setInterval(() => {
      setLeft((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          setPhase("expired");
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase, ticket?.ticket]);

  /* Ilova skanerlaganini kuzatish. */
  useEffect(() => {
    if (phase !== "ready" || !ticket) return;
    const timer = window.setInterval(() => {
      authApi
        .qrStatus(ticket.ticket)
        .then((status) => {
          if (status.status === "used") {
            setDevice(status.device);
            setPhase("linked");
          } else if (status.status === "expired" || status.status === "cancelled") {
            setPhase("expired");
          }
        })
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [phase, ticket]);

  /* Ulangach oyna biroz turib o'zi yopiladi. */
  useEffect(() => {
    if (phase !== "linked") return;
    const timer = window.setTimeout(onClose, 3500);
    return () => window.clearTimeout(timer);
  }, [phase, onClose]);

  return (
    <div className="qr-link">
      {phase === "ready" && ticket ? (
        <>
          <div className="qr-link-code" dangerouslySetInnerHTML={{ __html: ticket.qr_svg }} />
          <p className={`qr-link-timer${left <= 30 ? " is-low" : ""}`} role="timer" aria-live="off">
            <Icon name="clock" size={14} /> Amal qilish muddati: <b className="num">{formatTime(left)}</b>
          </p>
          <ol className="qr-link-steps">
            <li>Telefonda <b>Smart Forest</b> ilovasini oching.</li>
            <li>«QR orqali kirish» ni bosing va kodni kameraga ko'rsating.</li>
            <li>Ilova shu hisob bilan avtomatik kirib ketadi.</li>
          </ol>
          <p className="qr-link-note">Kod bir marta ishlaydi. Uni boshqalarga yubormang.</p>
          <p className="qr-link-note">
            Ilova telefonda yo'qmi?{" "}
            <Link to={SECTION_PATHS["mobile-app"]} onClick={onClose}>
              Yuklab olish
            </Link>
          </p>
        </>
      ) : null}

      {phase === "loading" ? <p className="qr-link-state">QR-kod tayyorlanmoqda…</p> : null}

      {phase === "linked" ? (
        <div className="qr-link-state is-ok" role="status">
          <Icon name="shield" size={36} />
          <b>Mobil ilova ulandi</b>
          <span>{device || "Qurilma"} shu hisob bilan kirdi.</span>
        </div>
      ) : null}

      {phase === "expired" || phase === "error" ? (
        <div className="qr-link-state" role="status">
          <b>{phase === "expired" ? "QR-kod muddati tugadi" : "Xatolik"}</b>
          <span>{phase === "expired" ? "Xavfsizlik uchun kod 5 daqiqadan keyin bekor bo'ladi." : message}</span>
          <button type="button" className="btn btn-primary" onClick={() => void issue()}>
            <Icon name="refresh" size={16} /> Yangi QR-kod
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function MobileLinkButton({ variant = "icon" }: { variant?: "icon" | "pill" }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      {variant === "pill" ? (
        <button type="button" className="btn btn-ghost btn-sm mobile-link-btn" onClick={() => setOpen(true)}>
          <Icon name="smartphone" size={16} />
          Mobil ilova
        </button>
      ) : (
        <button
          type="button"
          className="icon-btn mobile-link-btn"
          aria-label="Mobil ilovaga ulash"
          title="Mobil ilovaga ulash (QR)"
          onClick={() => setOpen(true)}
        >
          <Icon name="smartphone" size={18} />
        </button>
      )}
      {/* Portal: yuqori panelda `backdrop-filter` bor — `position: fixed` oyna panel ichida qolib ketardi. */}
      {createPortal(
        <Modal open={open} title="Mobil ilovaga ulash" onClose={close} size="sm">
          {open ? <MobileLinkDialog onClose={close} /> : null}
        </Modal>,
        document.body,
      )}
    </>
  );
}
